import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { themeGet } from '@styled-system/theme-get';

import { imgOnError } from '../../../../../javascript/shared/helpers';
import { useReportsSurvey } from '../data';
import { useTileStatusTabs } from '../../hooks/useTileStatusTabs';
import {
  Th,
  TableIcon,
  Td as StyledTd,
  Tr,
  ShadedTr,
} from '../../../../shared/TableComponents/StyledComponents';
import Card from '../../../../shared/Card';
import Placeholder from '../../../../shared/Placeholder';
import Tooltip from '../../../../shared/Tooltip';
import Text from '../../../../shared/Text';
import PercentBar from '../../../../shared/PercentBar';
import Sort from '../../../../shared/Sort';

const Table = styled.table`
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
`;

const Td = styled(StyledTd)`
  vertical-align: top;
  position: relative;
  top: 1px;
  &:after {
    content: '';
    position: absolute;
    top: -1px;
    left: 0px;
    height: 1px;
    width: 100%;
    background-color: ${themeGet('colors.black.10')};
  }
  &:first-child:after {
    width: calc(100% - 24px);
    right: 0px;
    left: auto;
  }
  &:last-child:after {
    width: calc(100% - 24px);
  }
`;

const AnswerWrapper = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 100px 47px;
  padding: 8px 0;
  border-bottom: 1px solid;
  border-color: ${themeGet('colors.black.10')};

  &:first-child {
    padding: 0 0 8px;
  }
`;

const PercentageWrapper = styled.div`
  width: 97px;
  display: flex;
`;

const Answers = styled.div`
  margin: 12px 0 0;
`;

const TotalWrapper = styled.div`
  display: flex;
  margin: 8px 0 24px;
  justify-content: space-between;
`;

const Icon = styled(TableIcon)`
  margin-top: 12px;
`;

const SortWrapper = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  cursor: pointer;
  align-items: center;
  gap: 8px;
`;

const SurveyTable = ({ data, sortingStatus, changeSort }) => {
  const { t } = useTranslation('reports');

  return (
    <div style={{ width: '100%', paddingTop: '15px' }}>
      <Tooltip id="Survey" />

      <Table>
        <thead>
          <Tr>
            <Th style={{ width: '72px' }}>
              <Text
                data-tip={t('surveyTiles.tableColumns.tileTooltip')}
                data-for="Survey"
                variant="body3"
              >
                {t('surveyTiles.tableColumns.tile')}
              </Text>
            </Th>
            <Th>
              <SortWrapper onClick={() => changeSort('headline')}>
                <Sort sortingStatus={sortingStatus} columnName="headline" />
                <Text
                  data-tip={t('surveyTiles.tableColumns.headlineTooltip')}
                  data-for="Survey"
                  variant="body3"
                >
                  {t('surveyTiles.tableColumns.headline')}
                </Text>
              </SortWrapper>
            </Th>
            <Th>
              <Text
                data-tip={t('surveyTiles.tableColumns.questionTooltip')}
                data-for="Survey"
                variant="body3"
              >
                {t('surveyTiles.tableColumns.question')}
              </Text>
            </Th>
            <Th>
              <Text
                data-tip={t('surveyTiles.tableColumns.answersTooltip')}
                data-for="Survey"
                variant="body3"
              >
                {t('surveyTiles.tableColumns.answers')}
              </Text>
            </Th>
          </Tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <ShadedTr
              key={i}
              className={`stat_action js-open-tile-stats-modal`}
              data-tile-id={row.id}
              data-href={`/client_admin/tiles/${row.id}/tile_stats`}
              data-tab="Reports tab"
              data-sub-tab="Survey Tiles"
              style={{ cursor: 'pointer' }}
            >
              <Td textAlign="left">
                <Icon src={row.tileImage} onError={imgOnError} />
              </Td>
              <Td>
                <Text
                  as="a"
                  display="inline-block"
                  color="primary.100"
                  my="12px"
                  variant="small1"
                >
                  {row.headline}
                </Text>
              </Td>
              <Td>
                <Text as="p" my="12px" variant="small1">
                  {row.question}
                </Text>
              </Td>
              <Td>
                <Answers>
                  {row.answers.map(({ text, amount, percentage, index }) => (
                    <AnswerWrapper key={index}>
                      <Text variant="small1" mr="auto">
                        {text}
                      </Text>
                      <PercentageWrapper>
                        <PercentBar percentage={`${percentage}%`} />
                      </PercentageWrapper>
                      <Text variant="small1" width="48px" textAlign="right">
                        {amount}
                      </Text>
                    </AnswerWrapper>
                  ))}
                  <TotalWrapper>
                    <Text variant="button">
                      {t('surveyTiles.totalAnswers')}
                    </Text>
                    <Text variant="button" textAlign="right">
                      {row.totalAnswersCount}
                    </Text>
                  </TotalWrapper>
                </Answers>
              </Td>
            </ShadedTr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export const Survey = ({
  onClick,
  showAll,
  campaignId,
  sortingStatus,
  changeSort,
}) => {
  const {
    activeTab,
    tileStatus,
    page,
    tabs,
    setPage,
    handleTabClick,
  } = useTileStatusTabs('survey');
  const { t } = useTranslation('reports');
  const itemsPerPage = 5;
  const onPageChange = data => setPage(data.selected + 1);
  const { isLoading, data } = useReportsSurvey({
    page,
    itemsPerPage,
    showAll,
    campaignId,
    tileStatus,
    ...sortingStatus,
  });

  const { pagy } = data;

  return (
    <Card
      items={!showAll && itemsPerPage}
      pagy={!showAll && data.data.length > 0 ? pagy : null}
      titleTooltip={t('surveyTiles.tableTitleTooltip')}
      title={t('surveyTiles.tableTitle')}
      onClick={onClick}
      canShowAll={data.data.length > 0}
      isShowingAll={showAll}
      isLoading={isLoading}
      onPageChange={onPageChange}
      className="survey-section"
      tabs={tabs}
      activeTab={activeTab}
      tabClick={handleTabClick}
      margin="0"
    >
      {data.data.length ? (
        <SurveyTable
          data={data.data}
          sortingStatus={sortingStatus}
          changeSort={changeSort}
        />
      ) : (
        <Placeholder />
      )}
    </Card>
  );
};
